import React, { useMemo } from 'react';
import PanelErrorBoundary from '../../shared/PanelErrorBoundary';
import { SkeletonText } from '../../shared/Skeleton';
import { useTileData } from '../../../hooks/useTileData';
import { getContactEngagement, suggestRecipient, suggestAccountOwner } from '../../../api/hub';
import { EmptyState, Pager, usePaged, tintClasses, fmtNumber } from '../kit';
import { SURFACE } from '../layout';
import CardHeader from './CardHeader';
import { BlockError, isPending } from './states';
import AcceptOwnerButton, { ACCEPT_BUTTON } from './AcceptOwnerButton';
import { initialsOf } from './clientFacts';
import { buildWriteTo, buildWhoAnswers, countPeople, formatAnswerTime } from './peopleModel';
import { canAssignAccountOwner } from './ownerCapability';
import { ownerStatsLine } from './ownerStats';
import CountBadge from './CountBadge';
import { ACCEPT_OWNER_TITLE, NO_OWNER_SUGGESTION } from './placeholders';

// One People card in three parts, replacing the three separate cards of the old
// page: WRITE TO (the top recipient suggestion, with reply speed), WHO ANSWERS (the
// other contacts, most engaged first) and ACCOUNT OWNER · SUGGESTED. Each part has
// its own source and its own loading / error / empty state, so one failing read
// never blanks the card. Every person is ONE line (avatar, name, mono meta at the
// right); the server's longer reasons are tooltips, not second lines.
//
// Sources: fn_suggest_recipient, mv_contact_engagement, fn_suggest_account_owner.

const QUIET_WARN_DAYS = 30;
const LABEL = 'text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const META = 'shrink-0 text-[13px] tabular-nums text-gray-500 dark:text-gray-400';

const Avatar = ({ name, muted = false }) => (
  <span
    aria-hidden="true"
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
      muted
        ? 'border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500'
        : 'border-gray-200 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
    }`}
  >
    {initialsOf(name)}
  </span>
);

const Loading = ({ lines = 1 }) => (
  <div role="status" aria-label="Loading"><SkeletonText lines={lines} lineClass="h-9" /><span className="sr-only">Loading…</span></div>
);

/** A titled part of the card, set off by a hairline; `region` gives it an accessible name. */
const Part = ({ title, region, children }) => (
  <section aria-label={region} className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-800">
    <h4 className={`${LABEL} mb-3`}>{title}</h4>
    {children}
  </section>
);

const StaleTag = () => (
  <span className="shrink-0 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">may have left</span>
);

/** "~1h · 855d quiet" — quiet days warn once a contact who used to reply has gone silent. */
const AnswerMeta = ({ hours, quietDays }) => {
  const parts = [];
  if (hours !== null && hours !== undefined) parts.push(<span key="h">{formatAnswerTime(hours)}</span>);
  if (quietDays !== null && quietDays !== undefined) {
    parts.push(
      <span key="q" className={quietDays >= QUIET_WARN_DAYS ? tintClasses('warn').text : ''}>{fmtNumber(quietDays)}d quiet</span>,
    );
  }
  return (
    <span className={META}>
      {parts.flatMap((p, i) => (i === 0 ? [p] : [' · ', p]))}
    </span>
  );
};

export { ownerStatsLine };

/**
 * @param {object} props
 * @param {string} props.clientId
 * @param {boolean} [props.canAssignOwner]  Offer a working Accept on the suggestion. Defaults to
 *   canAssignAccountOwner() — false until an assign RPC exists, and Accept is then a
 *   disabled, titled placeholder.
 * @param {() => void} [props.onOwnerAssigned]
 * @param {{data:*, loading:boolean, error:*, refresh:() => void}} [props.recipients]
 *   The useTileData result for fn_suggest_recipient, when the page already holds that read
 *   (the header's contact fallback uses it too): the card then makes no read of its own, so
 *   there is one call per page. Omitted, the card fetches it itself.
 */
export default function PeopleCard({ clientId, canAssignOwner = canAssignAccountOwner(), onOwnerAssigned, recipients: provided = null }) {
  const ownRecipients = useTileData(() => suggestRecipient(clientId), [clientId], !!clientId && !provided);
  const recipients = provided || ownRecipients;
  const engagement = useTileData(() => getContactEngagement(clientId), [clientId], !!clientId);
  const owners = useTileData(() => suggestAccountOwner(clientId), [clientId], !!clientId);

  const recipientRows = recipients.data?.data;
  const engagementRows = engagement.data?.data;

  // A failed source contributes nothing rather than blocking the others.
  const writeTo = useMemo(
    () => buildWriteTo(recipients.error ? [] : recipientRows, engagement.error ? [] : engagementRows),
    [recipientRows, engagementRows, recipients.error, engagement.error],
  );
  const whoAnswers = useMemo(
    () => buildWhoAnswers(recipients.error ? [] : recipientRows, engagement.error ? [] : engagementRows, writeTo?.recipient || null),
    [recipientRows, engagementRows, recipients.error, engagement.error, writeTo],
  );
  const { pageRows, page, setPage, pageCount, pageSize, total } = usePaged(whoAnswers, clientId);

  const topOwner = useMemo(
    () => [...(owners.data?.data || [])].sort((a, b) => (Number(b?.score) || 0) - (Number(a?.score) || 0))[0] || null,
    [owners.data],
  );

  const recipient = writeTo?.recipient;
  // The count pill waits for all three reads so it never counts up as they land.
  const settled = ![recipients, engagement, owners].some((r) => r.loading || isPending(r));
  const people = settled ? countPeople(writeTo, whoAnswers, topOwner) : 0;
  const ownerStats = topOwner ? ownerStatsLine(topOwner) : null;

  return (
    <PanelErrorBoundary label="People">
      <section aria-label="People" className={SURFACE}>
        <CardHeader
          title="People"
          badge={people > 0 ? <CountBadge count={people} noun="person" plural="people" testId="people-count" /> : null}
        />

        <div className="mt-1">
          <Part title="Write to" region="Write to">
            {recipients.loading || isPending(recipients) ? (
              <Loading />
            ) : recipients.error ? (
              <BlockError error={recipients.error} what="recipient suggestions" padding="p-0" message="Couldn't load a recipient suggestion." onRetry={recipients.refresh} />
            ) : !writeTo ? (
              <EmptyState variant="inset" padding="py-3" icon={null} message="No recipient to suggest for this client yet." />
            ) : (
              <div
                className={`flex items-center gap-3 -mx-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/60 ${writeTo.stale ? 'opacity-60' : ''}`}
                data-testid="write-to"
                title={recipient.why || undefined}
              >
                <Avatar name={recipient.contact || recipient.email} />
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate text-base font-normal text-gray-900 dark:text-gray-100">
                    {recipient.contact || recipient.email || '—'}
                  </span>
                  {writeTo.fastest && (
                    <span className="shrink-0 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400">
                      Fastest
                    </span>
                  )}
                  {writeTo.stale && <StaleTag />}
                </div>
                {writeTo.hours !== null && <span className={META}>{formatAnswerTime(writeTo.hours)}</span>}
              </div>
            )}
          </Part>

          <Part title="Who answers" region="Who answers">
            {engagement.loading || isPending(engagement) ? (
              <Loading lines={2} />
            ) : engagement.error ? (
              <BlockError error={engagement.error} what="this client's contacts" padding="p-0" message="Couldn't load this client's contacts." onRetry={engagement.refresh} />
            ) : whoAnswers.length === 0 ? (
              <EmptyState variant="inset" padding="py-3" icon={null} message="No tracked contacts for this client yet." />
            ) : (
              <ul className="space-y-3">
                {pageRows.map((p) => (
                  <li
                    key={p.id}
                    data-testid="who-answers-row"
                    title={p.why || undefined}
                    className={`flex items-center gap-3 -mx-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/60 ${p.stale ? 'opacity-60' : ''}`}
                  >
                    <Avatar name={p.name} muted={p.stale} />
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-base font-normal text-gray-900 dark:text-gray-100">{p.name}</span>
                      {p.stale && <StaleTag />}
                    </div>
                    <AnswerMeta hours={p.hours} quietDays={p.quietDays} />
                  </li>
                ))}
                {pageCount > 1 && (
                  <li><Pager page={page} pageCount={pageCount} pageSize={pageSize} total={total} onPage={setPage} /></li>
                )}
              </ul>
            )}
          </Part>

          <Part title="Account owner · suggested" region="Account owner">
            {owners.loading || isPending(owners) ? (
              <Loading />
            ) : owners.error ? (
              <BlockError error={owners.error} what="account-owner suggestions" padding="p-0" message="Couldn't load an account-owner suggestion." onRetry={owners.refresh} />
            ) : !topOwner ? (
              <div
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-800 dark:bg-gray-800/40"
                data-testid="owner-suggestion-empty"
              >
                <span aria-hidden="true" className="w-8 shrink-0 text-center text-lg text-gray-500 dark:text-gray-400">-</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-normal italic text-gray-500 dark:text-gray-400">{NO_OWNER_SUGGESTION}</p>
                  <p className="truncate text-[13px] text-gray-500 dark:text-gray-400">No correspondence to suggest one from</p>
                </div>
              </div>
            ) : (
              <div
                className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/40"
                data-testid="owner-suggestion"
              >
                <span aria-hidden="true" className="w-7 shrink-0 text-center text-lg font-medium text-gray-700 dark:text-gray-200">
                  {initialsOf(topOwner.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-normal text-gray-900 dark:text-gray-100">{topOwner.name}</p>
                  {/* Built from the two numbers when both exist; else the server-composed
                      sentence, verbatim and cut to one line, full text on hover. */}
                  {ownerStats && (
                    <p className="truncate text-[13px] text-gray-500 dark:text-gray-400" title={topOwner.why || undefined}>{ownerStats}</p>
                  )}
                </div>
                {canAssignOwner && topOwner.member_id ? (
                  <AcceptOwnerButton clientId={clientId} memberId={topOwner.member_id} onAssigned={() => { owners.refresh?.(); onOwnerAssigned?.(); }} />
                ) : (
                  <button type="button" disabled title={ACCEPT_OWNER_TITLE} className={`${ACCEPT_BUTTON} shrink-0`}>Accept</button>
                )}
              </div>
            )}
          </Part>
        </div>
      </section>
    </PanelErrorBoundary>
  );
}
